import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowLeft, Loader2, ShoppingBag } from "lucide-react";
import axios from "axios";
import confetti from "canvas-confetti";
import { useCart } from "@/hooks/useCart";

import { API } from "@/lib/api";

const POLL_MAX = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [record, setRecord] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let tries = 0;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setRecord(res.data);
        setAttempts(tries + 1);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          clear();
          confetti({
            particleCount: 160,
            spread: 80,
            origin: { y: 0.6 },
            colors: ["#84C5F4", "#FDE68A", "#FECACA", "#DDD6FE", "#10B981"],
          });
          return;
        }
        tries += 1;
        if (tries >= POLL_MAX) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      className="pt-32 md:pt-40 pb-24 min-h-screen relative overflow-hidden"
      data-testid="payment-success-page"
    >
      <div className="aurora" style={{ width: 480, height: 480, background: "#BAE6FD", top: "5%", left: "-8%" }} />
      <div className="aurora" style={{ width: 420, height: 420, background: "#FDE68A", bottom: "5%", right: "-10%" }} />

      <div className="max-w-2xl mx-auto px-6 relative text-center">
        {status === "polling" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-[2rem] bg-white border border-[#BAE6FD] p-10"
          >
            <Loader2 className="mx-auto text-[#60A5FA] animate-spin" size={44} />
            <h1 className="mt-6 font-display font-semibold text-3xl text-[#051937]">
              Duszek sprawdza Twoją płatność…
            </h1>
            <p className="mt-3 text-[#0b2a55]/70">
              To zajmuje kilka sekund. Nie zamykaj okna.
            </p>
            <p className="text-xs text-[#0b2a55]/50 mt-4">próba {attempts}/{POLL_MAX}</p>
          </motion.div>
        )}

        {status === "paid" && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-[2rem] bg-white border border-[#BAE6FD] p-10 md:p-14"
            data-testid="payment-success-confirmed"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: [0, -12, 12, -6, 6, 0] }}
              transition={{ delay: 0.2, duration: 0.9 }}
              className="w-20 h-20 mx-auto rounded-full bg-[#DCFCE7] grid place-items-center"
            >
              <CheckCircle className="text-[#10B981]" size={44} />
            </motion.div>
            <h1 className="mt-6 font-display font-semibold text-4xl md:text-5xl text-[#051937] leading-tight">
              Dziękujemy!
            </h1>
            <p className="mt-4 font-script text-2xl text-[#60A5FA]">
              „Duszek poleciał z Twoją paczką w chmurach 💌”
            </p>
            <p className="mt-6 text-[#0b2a55]/80">
              Zamówienie o numerze <span className="font-mono">{sessionId?.slice(-8)}</span>{" "}
              zostało przyjęte. Potwierdzenie wysłaliśmy na Twój adres e-mail.
            </p>
            {record && (
              <p className="mt-3 text-sm text-[#0b2a55]/60">
                Kwota: <span className="font-semibold">{(record.amount / 100).toFixed(2)} {record.currency?.toUpperCase()}</span>
              </p>
            )}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                data-testid="payment-success-home"
                className="inline-flex items-center gap-2 rounded-full bg-[#051937] text-white px-6 py-3 font-semibold"
              >
                <ArrowLeft size={16} /> Strona główna
              </Link>
              <Link
                to="/sklep"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-[#BAE6FD] text-[#051937] px-6 py-3 font-semibold"
              >
                Zobacz inne książki <ShoppingBag size={16} />
              </Link>
            </div>
          </motion.div>
        )}

        {(status === "timeout" || status === "error") && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-[2rem] bg-white border border-[#FECACA] p-10"
            data-testid="payment-success-timeout"
          >
            <h1 className="font-display font-semibold text-2xl md:text-3xl text-[#051937]">
              Twoja płatność jest w drodze
            </h1>
            <p className="mt-4 text-[#0b2a55]/80">
              Nie udało nam się jeszcze potwierdzić statusu. Nie martw się — jeśli otrzymałeś potwierdzenie z banku, zamówienie zostało zapisane.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#051937] text-white px-6 py-3 font-semibold"
            >
              <ArrowLeft size={16} /> Strona główna
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
}
